// Pure helpers shared by pages/InvoiceDetail.tsx and its ≤880px render,
// components/InvoiceDetailMobile.tsx (mobile overhaul Phase C §8.2).
// The mobile component can't import the page (ESM cycle), so anything both
// renders need lives here — mirrors lib/projectDetailShared.ts.

import type { InvoiceStatus } from '../../shared/types';
import type { MoneyMode } from './money';
import { formatDate } from './calendar';
import { formatMoneyExact } from './moneyFormat';

/** Strip the protocol for the rail's external-link display. */
export function linkLabel(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/** One secondary figure beside the big amount. */
export interface AmountFigure {
  label: string;
  value: string;
  /** Danger ink — the due date of an overdue invoice (mockup `.v.d`). */
  tone?: 'd';
}

/** The amount card's headline label: "Amount due · ex GST" / "Amount". */
export function amountLabel(status: InvoiceStatus, mode: MoneyMode): string {
  return `${status === 'paid' ? 'Amount' : 'Amount due'}${mode === 'ex' ? ' · ex GST' : ''}`;
}

/** The secondary figures, in the desktop card's order and with its labels. */
export function amountFigures(
  invoice: { gst_cents: number; amount_cents: number; due_date: string | null; paid_date: string | null },
  status: InvoiceStatus,
  mode: MoneyMode,
): AmountFigure[] {
  return [
    { label: mode === 'ex' ? '+ GST' : 'Incl. GST', value: formatMoneyExact(invoice.gst_cents) },
    ...(mode === 'ex'
      ? [{ label: 'Total incl. GST', value: formatMoneyExact(invoice.amount_cents) }]
      : []),
    {
      label: 'Due date',
      value: formatDate(invoice.due_date),
      ...(status === 'overdue' ? { tone: 'd' as const } : {}),
    },
    ...(status === 'paid' && invoice.paid_date
      ? [{ label: 'Paid', value: formatDate(invoice.paid_date) }]
      : []),
  ];
}
